import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { RunReferenceStatus } from '../../../../services/benchmarkService';
import { PanelError, SectionCard } from '../../BenchmarkPrimitives';
import { refusalGuidance } from '../refusalGuidance';
import { dsVar, ink } from '../../../../theme/tokens';

interface RunStatusPanelProps {
    status: RunReferenceStatus | null;
    latestStep?: string | null;
    error?: string | null;
    onRetry?: () => void;
    /** Back to the upload, to correct the file. Hidden when there is nowhere to go. */
    onResubmit?: () => void;
}

const ResubmitButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <Button
        onClick={onClick}
        variant="contained"
        startIcon={<ReplayIcon sx={{ fontSize: 18 }} />}
        sx={{
            mt: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem',
            backgroundColor: dsVar('accent'), color: dsVar('on-accent'), borderRadius: '8px',
            '&:hover': { backgroundColor: '#F5D149' }
        }}
    >
        Fix and resubmit
    </Button>
);

/**
 * Where a submitted sample stands, before there are results to show: still
 * running, or finished without being scored — and if so, why, in the loader's
 * own words with what to do about it.
 */
const RunStatusPanel: React.FC<RunStatusPanelProps> = ({ status, latestStep, error, onRetry, onResubmit }) => {
    if (error) return <PanelError message={error} onRetry={onRetry} />;

    if (!status || status.status === 'running') {
        return (
            <SectionCard
                title={status ? 'Benchmarking your sample' : 'Checking your submission'}
                subtitle="Every case is replayed through the triage chain. Eight cases take about a minute and a half; a few hundred take several minutes."
                icon={<HourglassTopOutlinedIcon fontSize="small" />}
            >
                <Box className="flex items-center gap-3" role="status">
                    <CircularProgress size={20} sx={{ color: dsVar('accent-fg') }} />
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft') }}>
                        {latestStep ? `Latest step: ${latestStep}` : 'Waiting for the run to report progress…'}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 1.5 }}>
                    This page opens the results as soon as they are written.
                </Typography>
            </SectionCard>
        );
    }

    if (status.status === 'complete') return null;

    if (status.preview) {
        return (
            <SectionCard
                title="This was a preview"
                subtitle="The sample was read and checked, and nothing was scored. Run it without preview to benchmark it."
                icon={<VisibilityOutlinedIcon fontSize="small" />}
                accent="#94A3B8"
            >
                {onResubmit && <ResubmitButton onClick={onResubmit} />}
            </SectionCard>
        );
    }

    const guidance = status.reason ? refusalGuidance(status.reason) : null;
    return (
        <SectionCard
            title="Your sample was not scored"
            subtitle={status.failedStep ? `Stopped at: ${status.failedStep}` : undefined}
            icon={<ErrorOutlineOutlinedIcon fontSize="small" />}
            accent="#F87171"
        >
            {status.reason ? (
                <>
                    <Box
                        sx={{
                            p: 2, borderRadius: '8px', border: '1px solid rgba(248, 113, 113, 0.35)',
                            backgroundColor: 'rgba(248, 113, 113, 0.08)',
                            fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', color: ink('#FCA5A5'),
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                        }}
                    >
                        {status.reason}
                    </Box>
                    {guidance && (
                        <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), mt: 1.5, lineHeight: 1.6 }}>
                            {guidance.advice}
                        </Typography>
                    )}
                </>
            ) : (
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft') }}>
                    The run finished without writing results, and no reason was recorded.
                </Typography>
            )}
            {onResubmit && <ResubmitButton onClick={onResubmit} />}
        </SectionCard>
    );
};

export default RunStatusPanel;
